import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getProject, getRegistryUrls, listEpisodes, getScriptAnalysis,
  uploadScript, appendScript, regenerateAllPrompts,
} from '../../api';
import { triggerExternalAnalysis, retryExternalAnalysis, ExternalAnalysisError, getExternalAnalysisStatus, computeAnalysisProgress } from '../../api/externalAnalysis';
import { isWaitingForShots } from '../project-detail/utils/episodeProgress';
import { shouldPollEpisode } from '../episode-detail/utils/episodePolling';
import {
  Project, Episode, ScriptAnalysis, User,
  RegistryCharacter, RegistrySceneProp, RegistryAct, RegistryGlobalSummaryAny, RegistryEpisodeSummary,
} from '../../types';
import { normalizeCharacters, normalizeSceneProps, normalizeActs, enrichEpisodes } from '../../utils/registryParser';
import { projectEpisodePath } from '../../utils/routes';
import HeaderProjectNav from '../../components/HeaderProjectNav';
import { useHeaderSlot } from '../../components/HeaderSlotContext';
import CostFloatingPill from '../../components/CostFloatingPill';
import EmptyState from '../../components/EmptyState';
import { useToast } from '../../components/Toast';
import { useScriptMenu } from '../project-detail/hooks/useScriptMenu';
import { useProjectConfigState } from '../project-detail/hooks/useProjectConfigState';
import { useForcedConfig } from '../project-detail/hooks/useForcedConfig';
import ProjectDetailHeaderSection from '../project-detail/components/ProjectDetailHeaderSection';
import EpisodeProgressCards from '../project-detail/components/EpisodeProgressCards';
import EpisodeContentModal from '../episode-detail/components/EpisodeContentModal';
import EditProjectDialog from '../project-detail/components/EditProjectDialog';
import StyleConfigDialog from '../project-detail/components/StyleConfigDialog';
import ScriptFormatGuideModal from '../project-detail/components/ScriptFormatGuideModal';
import CharactersTab from './components/CharactersTab';
import ScenePropsTab from './components/ScenePropsTab';
import ActsTab from './components/ActsTab';
import GlobalTab from './components/GlobalTab';
import EpisodesTab from './components/EpisodesTab';
import './ScriptSummaryPage.css';

type TabKey = 'characters' | 'sceneProps' | 'acts' | 'global' | 'episodes';
const TAB_KEYS: TabKey[] = ['characters', 'sceneProps', 'global', 'acts', 'episodes'];

interface RegistryData {
  characters: RegistryCharacter[];
  sceneProps: RegistrySceneProp[];
  acts: RegistryAct[];
  globalSummary: RegistryGlobalSummaryAny;
  episodes: RegistryEpisodeSummary[];
}

// 剧本上传 / 拆分处理中（尚未产出分集）的项目级状态。
const SCRIPT_PROCESSING_STATUSES = new Set([
  'uploading_to_tos', 'uploaded', 'splitting', 'downloading_episodes', 'uploading_episodes_to_tos',
]);

export default function ScriptSummaryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('scriptSummary');
  const { t: tp } = useTranslation('projectDetail');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');

  const [project, setProject] = useState<Project | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [analysis, setAnalysis] = useState<ScriptAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [episodePolling, setEpisodePolling] = useState(false);

  // external 资产分析启动 / 进度
  const [externalTaskId, setExternalTaskId] = useState<string | null>(null);
  const [lastFailedExternalTaskId, setLastFailedExternalTaskId] = useState<string | null>(null);
  const [lastFailedExternalReason, setLastFailedExternalReason] = useState<string | null>(null);
  const [startingAnalysis, setStartingAnalysis] = useState(false);
  const [generatingAllPrompts, setGeneratingAllPrompts] = useState(false);
  const [analysisPct, setAnalysisPct] = useState(0);
  // 进度速查面板展开/收起的用户手动覆盖：null=跟随自动；true=强制展开；false=强制收起。
  const [overviewOverride, setOverviewOverride] = useState<boolean | null>(null);

  // 外部分析的完整 registry summary
  const [data, setData] = useState<RegistryData | null>(null);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('characters');

  // 剧本面板状态（自资产页迁来）
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [appendFile, setAppendFile] = useState<File | null>(null);
  const [appending, setAppending] = useState(false);
  const [showAppendForm, setShowAppendForm] = useState(false);
  const [showFormatGuide, setShowFormatGuide] = useState(false);
  const { scriptMenuOpen, scriptMenuRef, setScriptMenuOpen } = useScriptMenu();

  // 顶栏配置弹窗
  const [editingProject, setEditingProject] = useState(false);
  const [styleConfigOpen, setStyleConfigOpen] = useState(false);
  // 点击分集卡片打开的「剧集原文」弹窗
  const [contentEpisode, setContentEpisode] = useState<Episode | null>(null);

  const loadProject = useCallback(async (projectId: string) => {
    try {
      const p = await getProject(projectId);
      setProject(p);
      setPolling(!!p.script && SCRIPT_PROCESSING_STATUSES.has(p.script.status));
      // 复用资产页逻辑：刷新时从服务端回填进行中 / 上次失败的 external 分析任务，
      // 避免重载后丢失进度入口（只在本地为空时回填，不覆盖刚触发的新 taskId）。
      setExternalTaskId(curr => curr ?? p.activeAnalysisTaskId ?? null);
      if (p.lastFailedExtTaskId) {
        setLastFailedExternalTaskId(prev => prev ?? p.lastFailedExtTaskId ?? null);
        setLastFailedExternalReason(prev => prev ?? p.lastFailedExtReason ?? null);
      }
    } catch (err) {
      console.error('Failed to load project:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEpisodes = useCallback(async (projectId: string) => {
    try {
      const list = await listEpisodes(projectId);
      setEpisodes(list);
      // 与单集页同源的 shouldPollEpisode：pending 仅在 promptGenerationStatus==='generating' 时
      // 才算进行中，避免「等待资产分析/等待生成分镜」长期空轮询；终态失败不轮询。
      setEpisodePolling(list.some(shouldPollEpisode));
    } catch (err) {
      console.error('Failed to load episodes:', err);
    }
  }, []);

  const loadAnalysis = useCallback(async (projectId: string) => {
    try {
      const a = await getScriptAnalysis(projectId);
      setAnalysis(a.status === 'not_started' ? null : a);
    } catch {
      // 分析结果不存在时静默忽略
    }
  }, []);

  const reloadAll = useCallback(() => {
    if (!id) return;
    loadProject(id);
    loadEpisodes(id);
    loadAnalysis(id);
  }, [id, loadProject, loadEpisodes, loadAnalysis]);

  useEffect(() => {
    if (id) reloadAll();
  }, [id, reloadAll]);

  // registry summary：仅 external 且分析就绪时加载完整剧本摘要
  const analysisReady = analysis?.status === 'completed' || analysis?.status === 'extracted';
  useEffect(() => {
    if (!id || project?.analysisMode !== 'external' || !analysisReady) {
      setData(null);
      return;
    }
    let cancelled = false;
    setRegistryLoading(true);
    getRegistryUrls(id)
      .then(raw => {
        if (cancelled) return;
        const characters = normalizeCharacters(raw.characters);
        const sceneProps = normalizeSceneProps(raw.sceneprops);
        setData({
          characters,
          sceneProps,
          acts: normalizeActs(raw.acts),
          globalSummary: (raw.global ?? {}) as RegistryGlobalSummaryAny,
          episodes: enrichEpisodes(raw.episodes, characters, sceneProps),
        });
      })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setRegistryLoading(false); });
    return () => { cancelled = true; };
  }, [id, project?.analysisMode, analysisReady]);

  // 轮询：剧本处理中 / 分集进行中 / 分析进行中 时刷新
  const analysisInProgress = analysis?.status === 'analyzing' || analysis?.status === 'generating_prompts' || analysis?.status === 'pending_normalization';
  useEffect(() => {
    if (!id || (!polling && !episodePolling && !analysisInProgress)) return;
    const interval = setInterval(() => {
      if (polling) loadProject(id);
      loadEpisodes(id);
      if (polling || analysisInProgress) loadAnalysis(id);
    }, 3000);
    return () => clearInterval(interval);
  }, [id, polling, episodePolling, analysisInProgress, loadProject, loadEpisodes, loadAnalysis]);

  // external 资产分析进度轮询（进度直接喂进「开始资产分析」按钮里的进度条）。
  // 替代原 ExternalAnalysisStatus 组件：成功 → 刷新拉分析结果；失败 → 记录原因可重试。
  useEffect(() => {
    if (!id || !externalTaskId) { setAnalysisPct(0); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const next = await getExternalAnalysisStatus(id, externalTaskId, controller.signal);
        if (cancelled) return;
        setAnalysisPct(computeAnalysisProgress(next));
        if (next.status === 'succeeded') {
          setExternalTaskId(null); setLastFailedExternalTaskId(null); setLastFailedExternalReason(null);
          reloadAll();
          return;
        }
        if (next.status === 'failed' || next.status === 'cancelled') {
          const reason = next.error
            || (next.failedStep ? tc('externalAnalysis.stepFailed', { step: next.failedStep }) : tc('externalAnalysis.statusFailed'));
          setLastFailedExternalTaskId(externalTaskId);
          setLastFailedExternalReason(reason);
          setExternalTaskId(null);
          toast.error(tp('analysis.externalAnalysisFailed', { reason }));
          reloadAll();
          return;
        }
        timer = setTimeout(poll, 5000);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        timer = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); controller.abort(); };
  }, [id, externalTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfg = useProjectConfigState(project, { onSaved: () => { if (id) loadProject(id); } });
  const { needsStyle, needsScript, forcedUploading, handleForcedScriptUpload } = useForcedConfig(project, {
    projectId: id,
    onUploaded: () => { if (id) { loadProject(id); loadEpisodes(id); } },
    onError: (msg) => toast.error(msg),
    uploadFailedMessage: tp('script.uploadFailed'),
  });

  // 顶栏「视频创作」段的跳转目标：优先上次看过的那一集，否则第一集；无分集则 null（置灰）。
  const storyboardEpisodeId = useMemo(() => {
    if (!id || episodes.length === 0) return null;
    const last = localStorage.getItem(`lastEpisode:${id}`);
    if (last && episodes.some(ep => ep.id === last)) return last;
    return [...episodes].sort((a, b) => a.index - b.index)[0]?.id ?? null;
  }, [id, episodes]);

  useHeaderSlot(
    project && id
      ? <HeaderProjectNav
          projectId={id}
          active="script"
          storyboardEpisodeId={storyboardEpisodeId}
          project={project}
          currentUser={currentUser}
          memberCount={project.members?.length ?? 0}
          onEditProject={() => setEditingProject(true)}
          onStyleConfig={() => setStyleConfigOpen(true)}
          forced={needsStyle || needsScript}
          scriptStep={needsScript}
        />
      : null,
    project && id ? <CostFloatingPill projectId={id} variant="inline" /> : null,
    [id, !!project, project?.members?.length, storyboardEpisodeId, needsStyle, needsScript],
  );

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !id) return;
    if (!project?.baseConfigCompleted) {
      toast.warning(tp('script.completeStyleConfigToast'));
      return;
    }
    setUploading(true);
    try {
      await uploadScript(id, selectedFile);
      setSelectedFile(null);
      setPolling(true);
      loadProject(id);
    } catch (err: any) {
      toast.error(err?.message || tp('script.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleAppend = async () => {
    if (!appendFile || !id) return;
    setAppending(true);
    try {
      const result = await appendScript(id, appendFile);
      toast.success(result.message);
      setAppendFile(null);
      setShowAppendForm(false);
      setPolling(true);
      loadProject(id);
      loadEpisodes(id);
      setTimeout(() => loadAnalysis(id), 2000);
    } catch (err: any) {
      toast.error(err?.message || tp('script.appendFailed'));
    } finally {
      setAppending(false);
    }
  };

  // 开始 / 重试 external 资产分析（效果同资产设定页 ExternalAnalysisPanel 的开始按钮）。
  const handleStartExternalAnalysis = async () => {
    if (!id || startingAnalysis) return;
    setStartingAnalysis(true);
    try {
      const result = lastFailedExternalTaskId
        ? await retryExternalAnalysis(id)
        : await triggerExternalAnalysis(id);
      setExternalTaskId(result.taskId);
      setLastFailedExternalTaskId(null);
      setLastFailedExternalReason(null);
    } catch (e) {
      const ERROR_CODE_KEYS: Record<string, string> = {
        pronoun_resolve_required: 'externalAnalysis.errorPronounResolveRequired',
        analysis_in_progress: 'externalAnalysis.errorAnalysisInProgress',
        script_required: 'externalAnalysis.errorScriptRequired',
        insufficient_credit: 'externalAnalysis.errorInsufficientCredit',
        internal_error: 'externalAnalysis.errorInternalError',
      };
      let msg: string;
      if (e instanceof ExternalAnalysisError) {
        const key = e.code ? ERROR_CODE_KEYS[e.code] : undefined;
        msg = key ? tc(key) : (e.message || tc('startFailed'));
      } else {
        msg = e instanceof Error ? (e.message || tc('startFailed')) : tc('startFailed');
      }
      toast.error(msg);
    } finally {
      setStartingAnalysis(false);
    }
  };

  // 一键给全部分集提交分镜提示词生成任务（仅在全部分集都「等待生成分镜」时可点）。
  const handleGenerateAllPrompts = async (onlyFailed = false) => {
    if (!id || generatingAllPrompts) return;
    setGeneratingAllPrompts(true);
    try {
      const res = await regenerateAllPrompts(id, onlyFailed);
      toast.success(t('generateAllShotPromptsSubmitted', { count: res.triggered }));
      setEpisodePolling(true);
      loadEpisodes(id);
    } catch (err: any) {
      toast.error(err?.message || tp('confirmDialogs.regenAllPrompts.failed'));
    } finally {
      setGeneratingAllPrompts(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <EmptyState
          icon=""
          description={tp('loading', { ns: 'common' })}
          action={<div className="loading-spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', margin: '0 auto' }} />}
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container">
        <EmptyState icon="&#10067;" title={tp('project.notExist')} />
      </div>
    );
  }

  const scriptProcessing = !!project.script && SCRIPT_PROCESSING_STATUSES.has(project.script.status);
  const isExternal = project.analysisMode === 'external';
  // external 资产分析「开始」按钮：external 模式、分析未完成、当前无进行中任务时显示。
  // 守卫与资产页一致：剧本须拆分完成、且分集代词消解完成后才可开始。
  const scriptReady = project.script?.status === 'completed';
  const pronounsResolved = !episodes.some(ep =>
    ep.status === 'pending_pronoun_resolve' || ep.status === 'resolving_pronouns' || ep.status === 'resolving_pronouns_failed');

  // 资产分析按钮（external 模式常驻）：未开始 / 进行中 / 完成 / 失败可重试。点过一次即不可再点（除非失败）。
  const analysisRunning = externalTaskId !== null;
  const analysisFailed = !!lastFailedExternalTaskId && !analysisRunning && !analysisReady;
  const analysisCanStart = !analysisRunning && !analysisReady && (analysisFailed || (scriptReady && pronounsResolved));
  const analysisHint = !analysisReady && !analysisRunning && !analysisFailed && scriptReady && !pronounsResolved
    ? t('waitPronounResolve')
    : (analysisFailed ? (lastFailedExternalReason || undefined) : undefined);
  // 进度 0% 说明外部平台尚未真正开始跑（还在队列里），显示排队文案而不是「资产分析中 0%」。
  const analysisLabel = analysisReady ? t('assetAnalysisDone')
    : analysisRunning ? (analysisPct > 0 ? t('assetAnalyzing', { pct: analysisPct }) : t('assetAnalysisQueued'))
    : analysisFailed ? t('retryAssetAnalysis')
    : t('startAssetAnalysis');

  // 分镜提示词按钮：分集很多，仅当全部分集都「等待生成分镜」时才首次出现，避免误触；
  // 点过后常驻显示进度，全完成后不可点，有失败可重试。
  // 按钮隐藏期间 allWaitingForShots 暂无引用，连同上面 showShotButton 一起在后端接口完成后恢复。
  // const allWaitingForShots = episodes.length > 0 && externalTaskId === null
  //   && episodes.every(ep => isWaitingForShots(ep, project, analysis?.status));
  // 只要还有分集在「等待生成分镜」（如用户已在单集页生成了一部分，剩余集仍等待），
  // 全局「生成全部分镜提示词」就应可点，提交剩余/全部，避免出现不可点的死按钮。
  const someWaitingForShots = externalTaskId === null
    && episodes.some(ep => isWaitingForShots(ep, project, analysis?.status));
  const shotTotal = episodes.length;
  const shotCompleted = episodes.filter(ep => ep.promptGenerationStatus === 'completed').length;
  const shotGenerating = episodes.some(ep => ep.promptGenerationStatus === 'generating');
  const shotFailed = episodes.some(ep => ep.promptGenerationStatus === 'failed');
  const shotStarted = episodes.some(ep => ep.promptGenerationStatus && ep.promptGenerationStatus !== 'pending');
  const shotAllDone = shotTotal > 0 && shotCompleted === shotTotal;
  const shotPct = shotTotal > 0 ? Math.round((shotCompleted / shotTotal) * 100) : 0;
  const shotRunning = shotGenerating || generatingAllPrompts;
  const shotFailedActive = shotFailed && !shotRunning && !shotAllDone;
  const shotCanStart = !shotRunning && !shotAllDone && (shotFailedActive || someWaitingForShots);
  // 暂时隐藏「生成全部分镜提示词」按钮：当前接口会重跑全部分镜，
  // 「仅重跑剩余分镜」需后端支持，待后端新接口完成后恢复为下面注释的判断。
  // const showShotButton = analysisReady && (allWaitingForShots || shotStarted);
  const showShotButton = false;
  const shotLabel = shotAllDone ? t('shotPromptsDone')
    : shotRunning ? t('shotPromptsGenerating', { completed: shotCompleted, total: shotTotal })
    : shotFailedActive ? t('retryShotPrompts')
    : t('generateAllShotPrompts');

  // 资产分析 + 分镜提示词 全部完成 → 默认自动收起分集卡片，只留 title + 两个完成态按钮（干净一条）。
  // 但只要有任意分集存在未处理评论红点，就不自动收起（进入页面即展开），便于即时看到/处理评论。
  // 用户可随时点击整条 title 栏手动展开/收起（overviewOverride 覆盖自动判定）。
  const hasPendingComments = episodes.some(ep => (ep.pendingCommentCount ?? 0) > 0);
  // 标题栏右侧展示的全剧总分镜数（各分集 totalShots 之和）
  const totalShotCount = episodes.reduce((sum, ep) => sum + (ep.totalShots ?? 0), 0);
  const shouldAutoCollapse = analysisReady && shotAllDone && !hasPendingComments;
  const overviewExpanded = overviewOverride ?? !shouldAutoCollapse;

  return (
    <div className="container">
      {/* 上方：基础信息 Title 栏 + 剧本上传面板（自资产页迁来） */}
      <ProjectDetailHeaderSection
        project={project}
        projectId={id}
        episodes={episodes}
        analysis={analysis}
        polling={polling}
        selectedFile={selectedFile}
        uploading={uploading}
        appendFile={appendFile}
        appending={appending}
        showAppendForm={showAppendForm}
        scriptMenuOpen={scriptMenuOpen}
        scriptMenuRef={scriptMenuRef}
        t={tp}
        toast={toast}
        setProject={setProject}
        setPolling={setPolling}
        setAppendFile={setAppendFile}
        setShowAppendForm={setShowAppendForm}
        setShowFormatGuide={setShowFormatGuide}
        setScriptMenuOpen={setScriptMenuOpen}
        handleAppend={handleAppend}
        handleUpload={handleUpload}
        loadProject={loadProject}
        loadEpisodes={loadEpisodes}
        loadAnalysis={loadAnalysis}
      />

      {/* 中间：全集分集状态卡片；尚无分集但剧本处理中时展示处理中提示 */}
      {episodes.length > 0 ? (
        <div className="card project-info-panel theme-left-border" style={{ width: '100%', borderRadius: '10px', padding: '1rem', margin: '0 0 1.25rem', '--_theme-color': 'var(--theme-script)', '--_theme-color-end': '#34d399' } as React.CSSProperties}>
          {/* 整条 title 栏可点：随时切换展开/收起（右侧无描边箭头作指示） */}
          <div
            onClick={() => setOverviewOverride(!overviewExpanded)}
            role="button"
            aria-expanded={overviewExpanded}
            title={overviewExpanded ? t('collapseEpisodes') : t('expandEpisodes')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
          >
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, color: 'var(--theme-script)' }}>{t('episodeOverviewTitle')}</h3>

            {/* 评论监控：任意分集存在未处理评论红点时显示红底白字胶囊 */}
            {hasPendingComments && (
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: '999px', background: '#ef4444', color: '#fff', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.6, flexShrink: 0 }}>
                {t('newComments')}
              </span>
            )}

            {/* 资产分析：开始→进度→完成(常驻不可点)，失败可重试，进度条嵌在按钮里 */}
            {isExternal && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (analysisCanStart) handleStartExternalAnalysis(); }}
                disabled={!analysisCanStart}
                title={analysisHint}
                style={{ position: 'relative', overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '116px', gap: '6px', border: 'none', borderRadius: '6px', padding: '5px 12px', fontSize: '0.82rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, var(--theme-project), #60a5fa)', cursor: analysisCanStart ? 'pointer' : 'default', opacity: analysisCanStart || analysisRunning || analysisReady ? 1 : 0.55 }}
              >
                {(analysisRunning || analysisReady) && (
                  <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${analysisReady ? 100 : analysisPct}%`, background: 'rgba(255,255,255,0.28)', transition: 'width 0.3s ease' }} />
                )}
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {(startingAnalysis || analysisRunning) && <span className="loading-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />}
                  {analysisReady && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  {analysisLabel}
                </span>
              </button>
            )}

            {/* 分镜提示词：全部等待时首次出现→进度→完成(常驻不可点)，失败可重试 */}
            {showShotButton && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (shotCanStart) handleGenerateAllPrompts(shotFailedActive); }}
                disabled={!shotCanStart}
                style={{ position: 'relative', overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '140px', gap: '6px', border: 'none', borderRadius: '6px', padding: '5px 12px', fontSize: '0.82rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, var(--theme-storyboard), #3b82f6)', cursor: shotCanStart ? 'pointer' : 'default', opacity: 1 }}
              >
                {(shotRunning || shotAllDone || shotStarted) && (
                  <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${shotPct}%`, background: 'rgba(255,255,255,0.28)', transition: 'width 0.3s ease' }} />
                )}
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {shotRunning && <span className="loading-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />}
                  {shotAllDone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  {shotLabel}
                </span>
              </button>
            )}

            {/* 最右侧：总分镜数 + 展开/收起箭头指示（无描边，点击由整条 title 栏统一处理）。展开时朝上=点击收起 */}
            {totalShotCount > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-2)', flexShrink: 0 }}>
                {t('totalShotCount', { count: totalShotCount })}
              </span>
            )}
            <span style={{ marginLeft: totalShotCount > 0 ? undefined : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: overviewExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </div>

          {/* 分集卡片：展开时展示（全部完成默认自动收起，可随时手动展开/收起） */}
          {overviewExpanded && (
            <div style={{ marginTop: '0.85rem' }}>
              <EpisodeProgressCards episodes={episodes} project={project} analysisStatus={analysis?.status} onSelect={setContentEpisode} />
            </div>
          )}
        </div>
      ) : scriptProcessing ? (
        <section style={{ marginBottom: '1.25rem' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem',
            background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px',
          }}>
            <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>{t('processingTitle')}</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-2)', marginTop: '0.2rem' }}>{t('processingDesc')}</div>
            </div>
          </div>
        </section>
      ) : null}

      {/* 下方：external 且 registry 就绪时展示完整剧本摘要（同款左侧渐变描边白底圆角容器） */}
      {isExternal && analysisReady && (
        <section className="card project-info-panel theme-left-border" style={{ width: '100%', borderRadius: '10px', padding: '1rem', margin: 0, '--_theme-color': 'var(--theme-script)', '--_theme-color-end': '#34d399' } as React.CSSProperties}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.75rem', color: 'var(--theme-script)' }}>{t('summaryTitle')}</h3>
          {registryLoading && (
            <div className="script-summary-status">
              <span className="loading-spinner" />
              <span>{t('loading')}</span>
            </div>
          )}
          {data && !registryLoading && (
            <>
              <div className="script-summary-tabs">
                {TAB_KEYS.map((key) => (
                  <button
                    key={key}
                    className={`script-summary-tab ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {t(`tabs.${key}`)}
                  </button>
                ))}
              </div>
              <div className="script-summary-content">
                {activeTab === 'characters' && <CharactersTab characters={data.characters} />}
                {activeTab === 'sceneProps' && <ScenePropsTab sceneProps={data.sceneProps} />}
                {activeTab === 'acts' && <ActsTab acts={data.acts} />}
                {activeTab === 'global' && <GlobalTab globalSummary={data.globalSummary} />}
                {activeTab === 'episodes' && <EpisodesTab episodes={data.episodes} />}
              </div>
            </>
          )}
        </section>
      )}

      {/* 顶栏配置弹窗 + 剧本引导（含硬强制 Step3/Step4） */}
      <EditProjectDialog
        cfg={cfg}
        open={editingProject}
        onClose={() => setEditingProject(false)}
        analysisStarted={!!analysis}
      />
      <StyleConfigDialog
        cfg={cfg}
        projectId={id}
        open={styleConfigOpen || needsStyle}
        dismissible={!needsStyle}
        onClose={() => setStyleConfigOpen(false)}
      />
      <ScriptFormatGuideModal
        open={showFormatGuide || needsScript}
        dismissible={!needsScript}
        uploading={needsScript && forcedUploading}
        t={tp}
        onClose={() => setShowFormatGuide(false)}
        onSelectFile={(file) => {
          if (needsScript) {
            handleForcedScriptUpload(file);
          } else {
            setSelectedFile(file);
            setShowFormatGuide(false);
          }
        }}
        onInvalidFile={(messageKey = 'script.fileFormatOnly') => toast.warning(tp(messageKey))}
      />

      {/* 分集卡片点击 → 剧集原文弹窗（含查看/编辑/重新改写全部功能） */}
      <EpisodeContentModal
        episode={contentEpisode}
        project={project}
        episodes={episodes}
        open={!!contentEpisode}
        onClose={() => setContentEpisode(null)}
        onNavigate={setContentEpisode}
        onCreateVideo={(episode) => {
          setContentEpisode(null);
          navigate(projectEpisodePath(project.id, episode.id));
        }}
        onDirectorAnnotations={(episode) => {
          setContentEpisode(null);
          navigate(`/projects/${encodeURIComponent(project.id)}/episodes/${encodeURIComponent(episode.id)}/director-annotations`);
        }}
        onChanged={(taskStarted) => {
          // 重新改写=异步任务：立即开轮询，让 poll 循环持续拉「改写中」进度，避免后端状态
          // 还没体现就 loadEpisodes 一次把 episodePolling 算成 false 而漏掉进度。
          // 仅保存时直接刷新一次即可。
          if (taskStarted) setEpisodePolling(true);
          else if (id) loadEpisodes(id);
        }}
      />
    </div>
  );
}
